import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";
import { careersApi } from "../../services/careersApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";
import { PublicHeader } from "../../components/PublicHeader";
import { EMPTY_WEEKLY_AVAILABILITY } from "../../constants/interviewScheduling";
import { ContactStep } from "../../components/careers/wizard/ContactStep";
import { WorkAuthorizationStep } from "../../components/careers/wizard/WorkAuthorizationStep";
import { AvailabilityStep } from "../../components/careers/wizard/AvailabilityStep";
import { InterviewStep } from "../../components/careers/wizard/InterviewStep";

const EMPTY_FORM = {
  legal_name: "", email: "", phone: "", notes: "",
  work_authorization_status: "", work_authorization_other_detail: "", work_authorization_ack: false,
  shift_interests: [], shifts_per_week: "", can_work_min_shifts: "",
  weekly_availability: EMPTY_WEEKLY_AVAILABILITY,
  earliest_start_date: "", scheduling_restrictions: "",
  interview_hold: null,
  interview_contact_me: false,
  shift_premium_ack: false,
};

const SectionHeading = ({ index, children }) => (
  <div className="flex items-center gap-3 mb-4 mt-10 first:mt-0">
    <span className="w-7 h-7 rounded-full bg-blue-950 text-white text-xs font-bold flex items-center justify-center shrink-0">{index}</span>
    <h2 className="text-base sm:text-lg font-bold text-blue-950">{children}</h2>
  </div>
);

export default function ApplyPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLocalization();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [resumeFile, setResumeFile] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    careersApi.getJob(jobId).then((r) => setJob(r.data)).catch(() => setJob(null)).finally(() => setLoading(false));
  }, [jobId]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = () => {
    if (!form.legal_name.trim() || !form.email.trim() || !form.phone.trim()) return t("careers.wizard.errorContact");
    if (!form.work_authorization_status) return t("careers.wizard.errorWorkAuthStatus");
    if (form.work_authorization_status === "OTHER" && !form.work_authorization_other_detail?.trim()) return t("careers.wizard.errorWorkAuthOther");
    if (!form.work_authorization_ack) return t("careers.wizard.errorWorkAuthAck");
    if (!form.shifts_per_week || !form.can_work_min_shifts) return t("careers.wizard.errorAvailability");
    if (job.has_shift_premium && !form.shift_premium_ack) return t("careers.wizard.errorShiftPremiumAck");
    if (!form.interview_contact_me && !form.interview_hold) return t("careers.wizard.errorInterview");
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      document.getElementById("apply-page-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        job_posting_id: job.id,
        legal_name: form.legal_name, email: form.email, phone: form.phone, notes: form.notes,
        preferred_language: language,
        work_authorization_ack: form.work_authorization_ack,
        work_authorization_status: form.work_authorization_status,
        work_authorization_other_detail: form.work_authorization_other_detail || null,
        shift_interests: form.shift_interests,
        shifts_per_week: form.shifts_per_week,
        can_work_min_shifts: form.can_work_min_shifts,
        weekly_availability: form.weekly_availability,
        earliest_start_date: form.earliest_start_date || null,
        scheduling_restrictions: form.scheduling_restrictions || null,
        interview_contact_me: form.interview_contact_me,
        interview_hold_token: form.interview_hold?.hold_token || null,
        shift_premium_ack: form.shift_premium_ack,
      };
      const { data } = await careersApi.apply(payload);
      if (resumeFile) {
        try {
          const fd = new FormData();
          fd.append("file", resumeFile);
          await careersApi.uploadResume(data.id, fd);
        } catch {
          // Resume upload can be retried on the confirmation page - don't block the application itself.
        }
      }
      toast.success(t("careers.applicationReceived"));
      navigate(`/careers/applications/${data.id}`);
    } catch (err) {
      if (err.response?.status === 409 && form.interview_hold) update({ interview_hold: null });
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-white" data-testid="apply-page-loading" />;
  if (!job) {
    return (
      <div className="min-h-screen bg-white text-blue-950" data-testid="apply-page-not-found">
        <PublicHeader />
        <div className="flex flex-col items-center justify-center gap-4 py-24">
          <p className="text-gray-500">{t("careers.noJobsOpen")}</p>
          <Link to="/careers" className="text-amber-500 font-semibold hover:underline">{t("careers.backToJobs")}</Link>
        </div>
      </div>
    );
  }

  const title = (language === "ja" ? job.title_ja : job.title_en) || job.title_en;
  const stepProps = { form, update };

  return (
    <div className="min-h-screen bg-white text-blue-950" data-testid="apply-page">
      <PublicHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button data-testid="apply-page-back-link" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-950 mb-6 transition-colors">
          <ArrowLeft size={14} /> {t("careers.backToJobs")}
        </button>

        <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2">{t("careers.applicationForm")}</p>
        <h1 data-testid="apply-page-job-title" className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-8 break-words">{title}</h1>

        <SectionHeading index={1}>{t("careers.wizard.sectionContact")}</SectionHeading>
        <ContactStep {...stepProps} />

        <SectionHeading index={2}>{t("careers.wizard.sectionWorkAuthorization")}</SectionHeading>
        <WorkAuthorizationStep {...stepProps} />

        <SectionHeading index={3}>{t("careers.wizard.sectionAvailability")}</SectionHeading>
        <AvailabilityStep {...stepProps} />

        {job.has_shift_premium && (
          <label className="flex items-start gap-2.5 text-sm text-gray-700 bg-red-50 border border-red-200 rounded-xl p-3.5 mt-4" data-testid="shift-premium-ack-row">
            <input
              data-testid="shift-premium-ack-checkbox"
              type="checkbox"
              checked={form.shift_premium_ack}
              onChange={(e) => update({ shift_premium_ack: e.target.checked })}
              className="mt-0.5 shrink-0"
            />
            <span>{t("careers.shiftPremiumAckLabel")}</span>
          </label>
        )}

        <SectionHeading index={4}>{t("careers.wizard.sectionInterview")}</SectionHeading>
        <InterviewStep {...stepProps} job={job} />

        <SectionHeading index={5}>{t("careers.resumeLabel")}</SectionHeading>
        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5"><Paperclip size={13} /> {t("careers.resumeLabel")}</label>
        <input data-testid="apply-resume-input" type="file" accept=".pdf,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} className="w-full text-sm text-gray-600" />

        {error && <p id="apply-page-error" className="text-sm text-red-500 break-words mt-6" data-testid="career-application-error">{error}</p>}

        <button
          type="button"
          data-testid="submit-application-button"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 mt-6 py-4 rounded-full bg-blue-950 text-white font-semibold hover:bg-amber-400 hover:text-blue-950 active:scale-95 transition-all disabled:opacity-50"
        >
          <Send size={16} /> {submitting ? t("common.loading") : t("careers.submitApplication")}
        </button>
      </div>
    </div>
  );
}
